import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { useDomainServices } from "@/providers/DomainServices";

import {
  ChatMessageStatus,
  ChatRole,
  ChatStatus,
  ClientToolSpec,
  ClientViewingContext,
  isChatTextContent,
  type ChatRecord,
} from "../ChatRecord";

interface SendMessageParams {
  chatId: string;
  message: string;
  orgId: string;
  clientContext?: ClientViewingContext;
  clientTools?: ClientToolSpec[];
}

interface SendMessageContext {
  previousChats: Array<[ReadonlyArray<unknown>, ChatRecord | undefined]>;
  isRetry?: boolean;
  optimisticMessageCreated?: string;
}

function getRetryKey(chatId: string, message: string) {
  return `${chatId}:${message}`;
}

function findLastUserMessageIndex(
  messages: ChatRecord["messages"],
  messageText: string,
  options?: Partial<{
    created: string;
    status: ChatMessageStatus;
  }>,
) {
  for (let idx = messages.length - 1; idx >= 0; idx--) {
    const message = messages[idx];
    const textContent = message.content.find(isChatTextContent);

    if (message.role !== ChatRole.USER || textContent?.text !== messageText) {
      continue;
    }

    if (options?.created !== undefined && message.created !== options.created) {
      continue;
    }

    if (options?.status !== undefined && message.status !== options.status) {
      continue;
    }

    return idx;
  }

  return -1;
}

/**
 * Hook to send a new message to an existing chat conversation.
 *
 * After successfully sending a message, this hook will immediately refetch the chat
 * query to show the new message and start polling for the assistant response.
 *
 * @returns React Query mutation for sending messages
 */
export function useSendMessage() {
  const { chatService } = useDomainServices();
  const queryClient = useQueryClient();
  const optimisticContexts = useRef(
    new WeakMap<SendMessageParams, SendMessageContext>(),
  );
  const retryParams = useRef(new Map<string, SendMessageParams>());

  const applyOptimisticUpdate = ({ chatId, message }: SendMessageParams) => {
    const queryKey = ["chat", chatId] as const;

    void queryClient.cancelQueries({ queryKey });

    const previousChats = queryClient.getQueriesData<ChatRecord>({ queryKey });
    const created = new Date().toISOString();

    retryParams.current.delete(getRetryKey(chatId, message));

    // React Query may keep multiple cache entries for the same chatId
    // with different query key suffixes (for example, different
    // loadMessages values). Update every variant so the optimistic
    // message appears regardless of which cache entry the UI is reading.
    for (const [cachedQueryKey] of previousChats) {
      queryClient.setQueryData<ChatRecord>(cachedQueryKey, (previousChat) => {
        if (previousChat === undefined) {
          return previousChat;
        }

        return {
          ...previousChat,
          messages: [
            ...previousChat.messages,
            {
              content: [{ text: message }],
              created,
              role: ChatRole.USER,
              status: ChatMessageStatus.COMPLETED,
            },
          ],
          status: ChatStatus.ROBOTO_TURN,
          continuation_token: "",
        };
      });
    }

    return {
      previousChats,
      optimisticMessageCreated: created,
    };
  };

  const applyRetryUpdate = (variables: SendMessageParams) => {
    const queryKey = ["chat", variables.chatId] as const;

    void queryClient.cancelQueries({ queryKey });

    const previousChats = queryClient.getQueriesData<ChatRecord>({ queryKey });
    let updatedFailedMessage = false;

    retryParams.current.delete(
      getRetryKey(variables.chatId, variables.message),
    );

    for (const [cachedQueryKey] of previousChats) {
      queryClient.setQueryData<ChatRecord>(cachedQueryKey, (previousChat) => {
        if (previousChat === undefined) {
          return previousChat;
        }

        const failedMessageIndex = findLastUserMessageIndex(
          previousChat.messages,
          variables.message,
          {
            status: ChatMessageStatus.FAILED,
          },
        );

        if (failedMessageIndex === -1) {
          return previousChat;
        }

        updatedFailedMessage = true;

        return {
          ...previousChat,
          messages: previousChat.messages.map((chatMessage, idx) => {
            if (idx !== failedMessageIndex) {
              return chatMessage;
            }

            return {
              ...chatMessage,
              status: ChatMessageStatus.COMPLETED,
            };
          }),
          status: ChatStatus.ROBOTO_TURN,
          continuation_token: "",
        };
      });
    }

    if (!updatedFailedMessage) {
      return applyOptimisticUpdate(variables);
    }

    return {
      previousChats,
      isRetry: true,
    };
  };

  const rollbackOptimisticUpdate = (context?: SendMessageContext) => {
    context?.previousChats.forEach(([queryKey, previousChat]) => {
      queryClient.setQueryData(queryKey, previousChat);
    });
  };

  const markOptimisticMessageAsFailed = (
    variables: SendMessageParams,
    context?: SendMessageContext,
  ) => {
    const queryKey = ["chat", variables.chatId] as const;
    const previousChatsByKey = new Map(
      context?.previousChats.map(([cachedQueryKey, previousChat]) => [
        JSON.stringify(cachedQueryKey),
        previousChat,
      ]) ?? [],
    );

    retryParams.current.set(
      getRetryKey(variables.chatId, variables.message),
      variables,
    );

    queryClient
      .getQueriesData<ChatRecord>({ queryKey })
      .forEach(([cachedQueryKey]) => {
        const previousChat = previousChatsByKey.get(
          JSON.stringify(cachedQueryKey),
        );

        queryClient.setQueryData<ChatRecord>(cachedQueryKey, (currentChat) => {
          const fallbackChat = previousChat ?? currentChat;

          if (fallbackChat === undefined) {
            return fallbackChat;
          }

          const failedMessageIndex =
            currentChat === undefined
              ? -1
              : findLastUserMessageIndex(
                  currentChat.messages,
                  variables.message,
                  {
                    created: context?.optimisticMessageCreated,
                  },
                );

          const failedMessage = {
            content: [{ text: variables.message }],
            created:
              context?.optimisticMessageCreated ?? new Date().toISOString(),
            role: ChatRole.USER,
            status: ChatMessageStatus.FAILED,
          };

          if (currentChat === undefined || failedMessageIndex === -1) {
            return {
              ...fallbackChat,
              messages: [...fallbackChat.messages, failedMessage],
              status: previousChat?.status ?? ChatStatus.USER_TURN,
              continuation_token:
                previousChat?.continuation_token ??
                fallbackChat.continuation_token,
            };
          }

          return {
            ...currentChat,
            messages: currentChat.messages.map((chatMessage, idx) => {
              if (idx !== failedMessageIndex) {
                return chatMessage;
              }

              return {
                ...chatMessage,
                status: ChatMessageStatus.FAILED,
              };
            }),
            status: previousChat?.status ?? ChatStatus.USER_TURN,
            continuation_token:
              previousChat?.continuation_token ??
              currentChat.continuation_token,
          };
        });
      });
  };

  const mutation = useMutation<
    void,
    Error,
    SendMessageParams,
    SendMessageContext
  >({
    mutationFn: async ({
      chatId,
      message,
      orgId,
      clientContext,
      clientTools,
    }) => {
      return chatService.sendMessage(chatId, message, {
        orgId,
        clientContext,
        clientTools,
      });
    },
    onMutate: (variables) => {
      const existingContext = optimisticContexts.current.get(variables);

      if (existingContext !== undefined) {
        optimisticContexts.current.delete(variables);
        return existingContext;
      }

      return applyOptimisticUpdate(variables);
    },
    onError: (_error, variables, context) => {
      if (context?.isRetry) {
        retryParams.current.set(
          getRetryKey(variables.chatId, variables.message),
          variables,
        );
        rollbackOptimisticUpdate(context);
        return;
      }

      markOptimisticMessageAsFailed(variables, context);
    },
    onSuccess: async (_, variables) => {
      // Immediately refetch the chat query to show the new message and start polling
      return queryClient.refetchQueries({
        queryKey: ["chat", variables.chatId],
      });
    },
  });

  const mutate: typeof mutation.mutate = (variables, options) => {
    optimisticContexts.current.set(variables, applyOptimisticUpdate(variables));
    return mutation.mutate(variables, options);
  };

  const mutateAsync: typeof mutation.mutateAsync = (variables, options) => {
    optimisticContexts.current.set(variables, applyOptimisticUpdate(variables));
    return mutation.mutateAsync(variables, options);
  };

  const retry = async (chatId: string, message: string) => {
    const variables = retryParams.current.get(getRetryKey(chatId, message));

    if (variables === undefined) {
      return undefined;
    }

    optimisticContexts.current.set(variables, applyRetryUpdate(variables));
    return mutation.mutateAsync(variables);
  };

  return {
    ...mutation,
    mutate,
    mutateAsync,
    retry,
  };
}
